import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { SNIPPET_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import { useIsSecurityEnabled } from '../../../../helpers/features';
import { FeatureFlagRequired } from '../../../generic';
import InlineCopiableText from '../../../generic/CopiableText/InlineCopiableText';
import FilterCardMultiSelect from '../../../generic/Filter/FilterCardMultiSelect';
import { FormGroup, LabeledSelect, LabeledTextArea, LabeledTextInput } from '../../../generic/Forms';
import Header from '../../../shared/settings/SettingsPageHeader';
import Subheading from '../../../shared/settings/SettingsPageSubheading';
import PageContent from '../shared/SettingsPageContent';

import BadgePanel from './BadgePanel';
import ContributorCount from './ContributorCount';
import DangerZone from './DangerZone';
import GitHubStatusChecks from './GitHubStatusChecks';
import SnippetAutoRejectThreshold from './SnippetAutoRejectThreshold';
import useProjectGeneralSettings from './useProjectGeneralSettings';
import type { ProjectGeneralSettings } from './types';

import styles from './GeneralSettings.module.less';

/**
 * ProjectGeneralSettingsContainer displays the general settings page for a project.
 * A lot of the settings are grouped into the "general" settings", so there are quite
 * a lot of them here.
 */
const ProjectGeneralSettingsContainer: VoidFunctionComponent = () => {
  const {
    locator,
    settings,
    canEdit,
    labels,
    branches,
    vcsHosts,
    integrationHookFailStates,
    isLoading,
    isSaving,
    isSaveButtonDisabled,
    onSave,
    onUpdate,
    onDeleteProject,
  } = useProjectGeneralSettings();

  const hasSecurity = useIsSecurityEnabled();

  return (
    <div className={styles.layout}>
      <span className={styles.layoutMain}>
        <PageContent isLoading={isLoading}>
          <Header heading="General">
            <Header.SaveButton
              isLoading={isSaving}
              isDisabled={isSaveButtonDisabled}
              onClick={onSave}
            />
          </Header>
          <Subheading>
            Basics
          </Subheading>
          <FormGroup>
            <LabeledTextInput
              label="Project Title"
              placeholder="my-project"
              value={settings.title}
              onChange={title => onUpdate({ title })}
              size="small"
              isDisabled={!canEdit}
            />
            <InlineCopiableText
              label="Project Locator"
              value={locator}
              size="small"
            />
            <LabeledTextInput
              label="Project URL"
              placeholder="https://github.com/fossas/fossa-cli"
              value={settings.url}
              onChange={url => onUpdate({ url })}
              size="small"
              isDisabled={!canEdit}
            />
            <LabeledTextArea
              label="Project Notes"
              placeholder="Enter notes here..."
              value={settings.notes}
              onChange={notes => onUpdate({ notes })}
              size="small"
              isDisabled={!canEdit}
            />
            <FilterCardMultiSelect
              label="Labels"
              placeholder="Select labels..."
              options={labels}
              values={settings.labels}
              onChange={updatedLabels => onUpdate({
                labels: updatedLabels.map(l => ({
                  id: l.value as number,
                  label: l.label,
                })),
              })}
              isDisabled={!canEdit || isSaving}
            />
            <LabeledSelect
              label="Default Branch"
              id="project-settings-default-branch"
              placeholder="master"
              onChange={defaultBranch => onUpdate({ default_branch: defaultBranch?.value as string ?? null })}
              options={branches}
              value={settings.default_branch}
              isDisabled={!canEdit || isSaving}
              isMulti={false}
            />
            <>
              {/* Fragment is needed here to group these two elements as one FormGroup child */}
              <FilterCardMultiSelect
                label="Tracking Branches"
                placeholder="Select tracking branches..."
                options={branches}
                values={settings.tracking_branches}
                onChange={updatedTrackingBranches => onUpdate({
                  tracking_branches: updatedTrackingBranches.map(l => l.value as string),
                })}
                isDisabled={!canEdit || isSaving}
              />
              <div className={`${styles.smallText} ${styles.smallTextInfo}`}>
                FOSSA will automatically analyze these branches when updates are triggered.
              </div>
            </>
            <FilterCardMultiSelect
              label="Ignored Dependencies"
              //  The placeholder says this because users are not able to actually add anything to this list
              placeholder="No ignored dependencies"
              isSearchable={false}
              menuIsOpen={false}
              options={[]}
              values={settings.transitive_excludes}
              onChange={ignoredDeps => onUpdate({
                transitive_excludes: ignoredDeps.map(l => l.value as string),
              })}
              isDisabled={!canEdit || isSaving}
            />
            <LabeledSelect
              label="VCS Host"
              id="project-settings-vcs-host"
              placeholder="Select version control system host..."
              onChange={vcsHost =>
                onUpdate({ vcs_host: vcsHost?.value as ProjectGeneralSettings['vcs_host'] ?? null })}
              options={vcsHosts}
              value={settings.vcs_host}
              isDisabled={!canEdit || isSaving}
              isClearable
            />
            <FeatureFlagRequired flagName={SNIPPET_DETECTION}>
              <SnippetAutoRejectThreshold
                canEdit={canEdit}
                isSaving={isSaving}
                threshold={settings.snippetAutoRejectMatchPercentageThreshold ?? undefined}
                onUpdate={(threshold?: number) => onUpdate({
                  snippetAutoRejectMatchPercentageThreshold: threshold ?? null,
                })}
              />
            </FeatureFlagRequired>
          </FormGroup>
          {settings.vcs_host?.value === 'github' && (
            <GitHubStatusChecks
              integrationhookTimeout={settings.integrationhook_timeout}
              integrationhookFailState={settings.integrationhook_fail_state}
              integrationHookFailStates={integrationHookFailStates}
              isSaving={isSaving}
              onUpdate={onUpdate}
            />
          )}
          <DangerZone
            locator={locator}
            isPublic={!!settings.public}
            isSaving={isSaving}
            onUpdatePrivacy={isPublic => onUpdate({ public: isPublic })}
            onDeleteProject={onDeleteProject}
          />
        </PageContent>
      </span>
      <span className={styles.layoutPanel}>
        <BadgePanel
          locator={locator}
          isSecurityEnabled={hasSecurity}
        />
        {!_.isNil(settings.contributorCount) && (
          <ContributorCount count={settings.contributorCount} />
        )}
      </span>
    </div>
  );
};

export default ProjectGeneralSettingsContainer;
